import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense, lazy } from "react";
import {
  Brain,
  TrendingUp,
  Sparkles,
  ArrowRight,
  Moon,
  Sun,
  Feather,
  Frown,
  Zap,
  User,
  Users,
  Smile,
  ChevronRight,
  Menu,
  X,
  Crown,
  Check,
} from "lucide-react";
import {
  journalQuestionPool,
  journalQuestionPoolDe,
  journalDemoEntryEn,
  journalDemoEntryDe,
} from "../../api/mockData";
import heroVideo from "../../assets/Create_a_second_cinematic_h.mp4";
import { useLanguage } from "../../i18n/LanguageContext";
import { captureLeadEmail } from "../../api/api";

// Lazy-load the analytics card so the recharts bundle is split out of the
// landing-page main chunk. Improves LCP and Lighthouse Performance.
const InteractiveAnalyticsCard = lazy(() => import("./InteractiveAnalyticsCard"));

/* -----------------------------------------------------------
 * Helpers
 * --------------------------------------------------------- */

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const isCaptureMode = () => {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("section");
};

const prefersReducedMotion = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const useScrollFade = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(() => isCaptureMode());
  const ref = useRef(null);
  useEffect(() => {
    if (isCaptureMode()) {
      setIsVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isVisible];
};

/* -----------------------------------------------------------
 * Public actions
 * --------------------------------------------------------- */

export const enterDemo = () => {
  try {
    localStorage.setItem("demoMode", "1");
  } catch (e) {
    // localStorage unavailable; continue with URL fallback
  }
  window.location.href = "/app?demo=1";
};

const PREMIUM_INTENT_KEY = "premium_intent";
const PREMIUM_INTENT_EXPIRY_KEY = "premium_intent_expires_at";

export const setPremiumIntent = (plan) => {
  try {
    localStorage.setItem(PREMIUM_INTENT_KEY, plan);
    localStorage.setItem(
      PREMIUM_INTENT_EXPIRY_KEY,
      String(Date.now() + 24 * 60 * 60 * 1000)
    );
  } catch (e) {
    // localStorage unavailable
  }
};

const goToAuth = (mode = "signup") => {
  window.location.href = `/auth?mode=${mode}`;
};

const reopenCookieSettings = () => {
  if (typeof window.openCookieSettings === "function") {
    window.openCookieSettings();
  } else {
    window.dispatchEvent(new CustomEvent("reflecta:open-cookie-settings"));
  }
};

/* -----------------------------------------------------------
 * Logo
 * --------------------------------------------------------- */

const ReflectaLogo = ({ className = "h-8 w-auto", showWordmark = true }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox={showWordmark ? "0 0 200 60" : "0 0 60 60"}
    className={className}
    aria-label="Reflecta"
    role="img"
  >
    <defs>
      <linearGradient id="reflectaLandingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#9B30FF" />
        <stop offset="100%" stopColor="#6A0DAD" />
      </linearGradient>
    </defs>
    <rect x="10" y="10" width="40" height="40" rx="10" ry="10" fill="url(#reflectaLandingGradient)" />
    <line x1="30" y1="10" x2="30" y2="50" stroke="#FFFFFF" strokeWidth="1.5" />
    <path d="M20 25 Q 25 18, 30 25 Q 35 32, 40 25" stroke="#FFFFFF" strokeWidth="2" fill="none" />
    <path
      d="M20 35 Q 25 42, 30 35 Q 35 28, 40 35"
      stroke="#FFFFFF"
      strokeWidth="2"
      fill="none"
      opacity="0.7"
    />
    {showWordmark && (
      <text
        x="60"
        y="38"
        fontFamily="'Plus Jakarta Sans', system-ui, sans-serif"
        fontWeight="600"
        fontSize="24"
        fill="#171717"
      >
        Reflecta
      </text>
    )}
  </svg>
);

/* -----------------------------------------------------------
 * MarqueeSection (D-02)
 * --------------------------------------------------------- */

const StarRow = () => (
  <div className="flex items-center gap-0.5" aria-label="5 out of 5 stars">
    {[0, 1, 2, 3, 4].map((i) => (
      <svg key={i} width="14" height="14" viewBox="0 0 20 20" fill="#9B30FF" aria-hidden="true">
        <path d="M10 15.27L16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z" />
      </svg>
    ))}
  </div>
);

const ReviewCard = ({
  initials,
  name,
  role,
  quote,
  color,
  metricLabel,
  metricValue,
}) => (
  <div className="flex-shrink-0 w-[360px] bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mx-3 flex flex-col gap-3 hover:shadow-md transition-shadow whitespace-normal">
    <StarRow />
    <p className="text-sm text-gray-800 leading-relaxed">
      &ldquo;{quote}&rdquo;
    </p>
    <div className="flex items-center gap-3 mt-auto pt-3 border-t border-gray-100">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
        style={{ background: color }}
        aria-hidden="true"
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-gray-900 truncate">{name}</div>
        <div className="text-xs text-gray-500 truncate">{role}</div>
      </div>
      {metricLabel && metricValue && (
        <div className="text-right flex-shrink-0">
          <div className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            {metricLabel}
          </div>
          <div className="text-sm font-bold text-[#6A0DAD]">{metricValue}</div>
        </div>
      )}
    </div>
  </div>
);

export const MarqueeSection = () => {
  const { t } = useLanguage();
  const pains = [
    {
      initials: "D",
      name: "Daria",
      role: t("landing.marquee.role.student", { city: "Frankfurt" }),
      color: "linear-gradient(135deg,#9B30FF,#6A0DAD)",
      metricLabel: t("landing.marquee.metric.streak"),
      metricValue: t("landing.marquee.metric.streakValue"),
      quote: t("landing.marquee.review1"),
    },
    {
      initials: "S",
      name: "Sharanya",
      role: t("landing.marquee.role.student", { city: "Dortmund" }),
      color: "linear-gradient(135deg,#3B82F6,#1E40AF)",
      metricLabel: t("landing.marquee.metric.entries"),
      metricValue: "82",
      quote: t("landing.marquee.review2"),
    },
    {
      initials: "A",
      name: "Andrii",
      role: t("landing.marquee.role.student", { city: "Mannheim" }),
      color: "linear-gradient(135deg,#10B981,#047857)",
      metricLabel: t("landing.marquee.metric.moodDelta"),
      metricValue: "+1.4",
      quote: t("landing.marquee.review3"),
    },
    {
      initials: "AK",
      name: "Aleks, 35",
      role: t("landing.marquee.role.founder"),
      color: "linear-gradient(135deg,#F59E0B,#B45309)",
      quote: t("landing.marquee.review4"),
    },
    {
      initials: "PN",
      name: "Priya, 29",
      role: t("landing.marquee.role.therapist"),
      color: "linear-gradient(135deg,#EC4899,#9D174D)",
      quote: t("landing.marquee.review5"),
    },
  ];

  return (
    <section
      id="d02-marquee"
      aria-label="People we built Reflecta for"
      className="w-full bg-gray-50 py-16 border-y border-gray-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 text-center">
        <h2 className="text-[#9B30FF] font-bold tracking-wide uppercase text-sm mb-2">
          {t("landing.marquee.eyebrow")}
        </h2>
        <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
          {t("landing.marquee.headline")}
        </h3>
      </div>
      <div className="w-full overflow-hidden">
        <div className="reflecta-marquee-wrapper relative flex w-full" tabIndex={0}>
          <div className="reflecta-marquee flex whitespace-nowrap">
            {[...pains, ...pains, ...pains].map((p, i) => (
              <ReviewCard key={i} {...p} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
 * Interactive Journal + Analytics (D-03 / D-04 / D-05)
 * --------------------------------------------------------- */

const CheckIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#22c55e"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

const RangeSlider = ({ name, label, value, onChange, minIcon, maxIcon }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700">
      {label}: <span className="font-bold text-[#6A0DAD]">{value}</span>
    </label>
    <div className="flex items-center gap-2 mt-1">
      {minIcon}
      <input
        type="range"
        name={name}
        min={1}
        max={5}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#6A0DAD]"
        aria-label={label}
      />
      {maxIcon}
    </div>
    <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
      <span>1</span>
      <span>5</span>
    </div>
  </div>
);

const InteractiveJournalCard = () => {
  const { t, language } = useLanguage();
  const demo = language === "de" ? journalDemoEntryDe : journalDemoEntryEn;
  const questionPool = language === "de" ? journalQuestionPoolDe : journalQuestionPool;
  const [title, setTitle] = useState(demo.title);
  const [content, setContent] = useState(demo.content);
  const [date] = useState(new Date().toISOString().split("T")[0]);
  const [sentiment, setSentiment] = useState(5);
  const [sleep, setSleep] = useState(5);
  const [stress, setStress] = useState(1);
  const [socialEngagement, setSocialEngagement] = useState(2);

  const [aiJournalingActive, setAiJournalingActive] = useState(true);
  const [aiQuestion, setAiQuestion] = useState("");
  const [loadingAiQuestion, setLoadingAiQuestion] = useState(false);
  const debounceTimeoutRef = useRef(null);

  const fetchAiQuestion = useCallback(async (currentContent) => {
    setLoadingAiQuestion(true);
    await new Promise((resolve) => setTimeout(resolve, 600));
    const seed = (currentContent || "").length;
    const question = questionPool[seed % questionPool.length];
    setAiQuestion(question);
    setLoadingAiQuestion(false);
  }, [questionPool]);

  useEffect(() => {
    if (aiJournalingActive) {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => {
        fetchAiQuestion(content);
      }, 1000);
    } else {
      setAiQuestion("");
    }
    return () => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, [aiJournalingActive, content, fetchAiQuestion]);

  return (
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 relative h-[640px] flex flex-col overflow-hidden">
      <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
            <Sparkles size={16} className="text-[#6A0DAD]" />
          </div>
          <h5 className="font-bold text-gray-900">{t("journal.newEntry")}</h5>
        </div>
        <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
          {t("landing.journal.card.live")}
        </span>
      </div>

      <div className="flex-1 min-h-0 flex flex-col gap-3 overflow-hidden">
        <div className="flex gap-3 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <label className="block text-xs font-medium text-gray-700 mb-1">{t("entry.title")}</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("entry.titlePlaceholder")}
              className="w-full h-9 bg-gray-50 rounded-lg border border-gray-200 px-3 text-sm font-medium text-gray-800 placeholder:text-gray-400 focus:bg-white focus:border-[#9B30FF] focus:ring-2 focus:ring-purple-200 outline-none transition-all"
            />
          </div>
          <div className="w-32 flex-shrink-0">
            <label htmlFor="d03-journal-date" className="block text-xs font-medium text-gray-700 mb-1">{t("entry.date")}</label>
            <input
              id="d03-journal-date"
              type="date"
              value={date}
              readOnly
              aria-label="Entry date"
              className="w-full h-9 bg-gray-50 rounded-lg border border-gray-200 px-2 text-xs text-gray-700 outline-none"
            />
          </div>
        </div>

        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-gray-700 mb-1">{t("entry.content")}</label>
          <div className="relative">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              placeholder={t("entry.contentPlaceholder")}
              className="w-full h-[112px] bg-gray-50 rounded-lg border border-gray-200 p-3 pb-10 text-sm text-gray-700 placeholder:text-gray-400 focus:bg-white focus:border-[#9B30FF] focus:ring-2 focus:ring-purple-200 outline-none transition-all resize-none"
            />
            <button
              type="button"
              onClick={() => setAiJournalingActive((v) => !v)}
              className={`absolute bottom-2 right-2 px-2.5 py-1 text-xs font-medium rounded-md flex items-center gap-1.5 transition-colors ${
                aiJournalingActive
                  ? "bg-purple-600 text-white hover:bg-purple-700"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300"
              }`}
              aria-pressed={aiJournalingActive}
            >
              <Sparkles className="h-3 w-3" />
              <span>{aiJournalingActive ? t("entry.aiOn") : t("entry.ai")}</span>
            </button>
          </div>
          <div className="mt-1.5 h-5 text-xs text-gray-600 overflow-hidden" aria-live="polite">
            {aiJournalingActive &&
              (loadingAiQuestion ? (
                <span className="italic text-gray-400 inline-flex items-center gap-2">
                  <span className="inline-block h-2 w-24 bg-gray-200 rounded animate-pulse" />
                  <span>{t("entry.aiLoading")}</span>
                </span>
              ) : (
                <p className="italic truncate">
                  {aiQuestion || t("entry.startTyping")}
                </p>
              ))}
          </div>
        </div>

        <div className="flex-shrink-0 pt-2 border-t border-gray-100">
          <h6 className="font-semibold text-gray-800 text-xs uppercase tracking-wider mb-2">
            {t("entry.stateTitle")}
          </h6>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <RangeSlider
              name="sentiment"
              label={t("entry.sentiment")}
              value={sentiment}
              onChange={setSentiment}
              minIcon={<Frown className="h-4 w-4 text-gray-500" />}
              maxIcon={<Smile className="h-4 w-4 text-gray-500" />}
            />
            <RangeSlider
              name="sleep"
              label={t("entry.sleepQuality")}
              value={sleep}
              onChange={setSleep}
              minIcon={<Moon className="h-4 w-4 text-gray-500" />}
              maxIcon={<Sun className="h-4 w-4 text-gray-500" />}
            />
            <RangeSlider
              name="stress"
              label={t("entry.stressLevel")}
              value={stress}
              onChange={setStress}
              minIcon={<Feather className="h-4 w-4 text-gray-500" />}
              maxIcon={<Zap className="h-4 w-4 text-gray-500" />}
            />
            <RangeSlider
              name="socialEngagement"
              label={t("entry.socialEngagement")}
              value={socialEngagement}
              onChange={setSocialEngagement}
              minIcon={<User className="h-4 w-4 text-gray-500" />}
              maxIcon={<Users className="h-4 w-4 text-gray-500" />}
            />
          </div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0">
        <button
          type="button"
          className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          className="px-4 py-2 bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] rounded-lg text-sm font-semibold text-white shadow hover:shadow-lg hover:shadow-purple-500/30 transition-all"
        >
          {t("entry.save")}
        </button>
      </div>
    </div>
  );
};

const AnalyticsCardFallback = () => (
  <div
    className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 h-[480px] flex items-center justify-center"
    aria-hidden="true"
  >
    <div className="w-full space-y-4">
      <div className="grid grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-gray-50 rounded-xl animate-pulse" />
        ))}
      </div>
      <div className="h-44 bg-gray-50 rounded-xl animate-pulse" />
      <div className="h-24 bg-gray-50 rounded-xl animate-pulse" />
    </div>
  </div>
);

const DeferredAnalyticsCard = () => {
  const wrapperRef = useRef(null);
  const [shouldMount, setShouldMount] = useState(() => isCaptureMode());

  useEffect(() => {
    if (shouldMount) return undefined;
    const reveal = () => setShouldMount(true);

    let observer;
    if (typeof IntersectionObserver !== "undefined" && wrapperRef.current) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            reveal();
            observer && observer.disconnect();
          }
        },
        { rootMargin: "400px 0px" }
      );
      observer.observe(wrapperRef.current);
    }

    const idleHandle =
      typeof window !== "undefined" && "requestIdleCallback" in window
        ? window.requestIdleCallback(reveal, { timeout: 4500 })
        : setTimeout(reveal, 3500);

    return () => {
      if (observer) observer.disconnect();
      if (
        typeof window !== "undefined" &&
        "cancelIdleCallback" in window &&
        typeof idleHandle === "number"
      ) {
        window.cancelIdleCallback(idleHandle);
      } else {
        clearTimeout(idleHandle);
      }
    };
  }, [shouldMount]);

  return (
    <div ref={wrapperRef}>
      {shouldMount ? (
        <Suspense fallback={<AnalyticsCardFallback />}>
          <InteractiveAnalyticsCard />
        </Suspense>
      ) : (
        <AnalyticsCardFallback />
      )}
    </div>
  );
};

export const FeatureSection = ({ onExploreAnalytics }) => {
  const { t } = useLanguage();
  const [ref1, isVisible1] = useScrollFade();
  const [ref2, isVisible2] = useScrollFade();
  const [ref3, isVisible3] = useScrollFade();

  return (
    <section id="features" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-[#9B30FF] font-bold tracking-wide uppercase text-sm mb-3">
            {t("landing.features.eyebrow")}
          </h2>
          <h3 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {t("landing.features.headline")}
          </h3>
          <p className="text-lg md:text-xl text-gray-500">
            {t("landing.features.description")}
          </p>
        </div>

        <div
          id="d03-journal"
          ref={ref1}
          className={`reflecta-fade-in flex flex-col lg:flex-row items-center gap-12 lg:gap-16 mb-24 lg:mb-32 transition-all duration-1000 transform ${
            isVisible1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="lg:w-1/2">
            <div className="bg-purple-100 w-12 h-12 rounded-xl flex items-center justify-center mb-6">
              <Smile className="text-[#6A0DAD]" size={24} />
            </div>
            <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {t("landing.journal.title")}
            </h4>
            <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
              {t("landing.journal.body")}
            </p>
            <ul className="space-y-4">
              {[
                t("landing.journal.bullet1"),
                t("landing.journal.bullet2"),
                t("landing.journal.bullet3"),
              ].map((item, i) => (
                <li key={i} className="flex items-center text-gray-700">
                  <div className="mr-3 bg-green-100 rounded-full p-1">
                    <CheckIcon />
                  </div>
                  <span className="font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:w-1/2 w-full">
            <InteractiveJournalCard />
          </div>
        </div>

        <div
          id="d04-analytics"
          ref={ref2}
          className={`reflecta-fade-in flex flex-col-reverse lg:flex-row items-center gap-12 lg:gap-16 mb-24 lg:mb-32 transition-all duration-1000 transform ${
            isVisible2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="lg:w-1/2 w-full">
            <DeferredAnalyticsCard />
          </div>

          <div className="lg:w-1/2">
            <div className="bg-purple-100 w-12 h-12 rounded-xl flex items-center justify-center mb-6">
              <TrendingUp className="text-[#6A0DAD]" size={24} />
            </div>
            <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {t("landing.analytics.title")}
            </h4>
            <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
              {t("landing.analytics.body")}
            </p>
            <button
              type="button"
              onClick={onExploreAnalytics}
              data-testid="d04-try-demo"
              className="inline-flex items-center text-[#9B30FF] font-semibold hover:text-[#6A0DAD] transition-colors group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2 rounded"
            >
              {t("landing.analytics.tryDemo")}
              <ArrowRight className="ml-2 transform group-hover:translate-x-1 transition-transform" size={18} />
            </button>
          </div>
        </div>

        <div
          id="d05-chatbot"
          ref={ref3}
          className={`reflecta-fade-in flex flex-col lg:flex-row items-center gap-12 lg:gap-16 transition-all duration-1000 transform ${
            isVisible3 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div id="science" className="lg:w-1/2">
            <div className="bg-purple-100 w-12 h-12 rounded-xl flex items-center justify-center mb-6">
              <Brain className="text-[#6A0DAD]" size={24} />
            </div>
            <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {t("landing.chatbot.title")}
            </h4>
            <ul className="space-y-3 mb-8">
              {[
                t("landing.chatbot.bullet1"),
                t("landing.chatbot.bullet2"),
                t("landing.chatbot.bullet3"),
              ].map((item, i) => (
                <li key={i} className="flex items-center text-base md:text-lg text-gray-700">
                  <div className="mr-3 bg-green-100 rounded-full p-1 flex-shrink-0">
                    <CheckIcon />
                  </div>
                  <span className="font-medium leading-snug">{item}</span>
                </li>
              ))}
            </ul>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-6 relative overflow-hidden group hover:shadow-md transition-all">
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#9B30FF] to-[#6A0DAD]" aria-hidden="true" />
              <p className="italic text-gray-700 text-sm">
                &ldquo;{t("landing.chatbot.insight")}&rdquo;
              </p>
              <div className="mt-4 flex items-center text-xs font-bold text-[#6A0DAD] uppercase tracking-wider">
                <Sparkles size={14} className="mr-2" /> {t("landing.chatbot.insightLabel")}
                <span className="ml-3 inline-flex items-center gap-1 text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full normal-case font-semibold">
                  <Crown size={10} /> {t("landing.pricing.premium")}
                </span>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2 w-full">
            <div className="bg-gradient-to-br from-[#9B30FF] to-[#6A0DAD] rounded-3xl p-1 shadow-2xl transform lg:rotate-2 lg:hover:rotate-0 transition-transform duration-500">
              <div className="bg-white rounded-[22px] p-6 md:p-8 h-full w-full">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center overflow-hidden">
                    <ReflectaLogo className="w-8 h-8" showWordmark={false} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{t("landing.chatbot.card.name")}</h4>
                    <p className="text-sm text-gray-500">{t("landing.chatbot.card.subtitle")}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-2xl rounded-tl-none p-4 w-[85%] text-sm text-gray-700">
                    {t("landing.chatbot.card.aiMsg1")}
                  </div>
                  <div className="bg-[#9B30FF] text-white rounded-2xl rounded-tr-none p-4 w-[85%] ml-auto text-sm shadow-md">
                    {t("landing.chatbot.card.userMsg")}
                  </div>
                  <div className="bg-gray-50 rounded-2xl rounded-tl-none p-4 w-[85%] text-sm text-gray-700">
                    {t("landing.chatbot.card.aiMsg2")}
                    <span className="inline-block w-1.5 h-3.5 bg-[#6A0DAD] ml-0.5 align-text-bottom animate-pulse" aria-hidden="true" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
 * PricingSection
 * --------------------------------------------------------- */

export const PricingSection = ({ onSelectPremium }) => {
  const { t } = useLanguage();
  const [annual, setAnnual] = useState(false);

  const features = [
    { label: t("landing.pricing.feature1"), free: true, premium: true },
    { label: t("landing.pricing.feature2"), free: true, premium: true },
    { label: t("landing.pricing.feature4"), free: false, premium: true },
    { label: t("landing.pricing.feature5"), free: false, premium: true },
    { label: t("landing.pricing.feature6"), free: false, premium: true },
    { label: t("landing.pricing.feature7"), free: false, premium: true },
    {
      freeLabel: t("landing.pricing.feature8Free"),
      premiumLabel: t("landing.pricing.feature8Premium"),
      free: true,
      premium: true,
    },
  ];

  const annualMonthly = "9.99";
  const monthly = "11.99";

  return (
    <section id="pricing" className="bg-white py-20 md:py-24 border-t border-gray-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <h2 className="text-[#9B30FF] font-bold tracking-wide uppercase text-sm mb-3">
            {t("landing.pricing.eyebrow")}
          </h2>
          <h3 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">
            {t("landing.pricing.headline")}
          </h3>
          <p className="text-base md:text-lg text-gray-500">
            {t("landing.pricing.description")}
          </p>

          <div className="mt-6 inline-flex items-center bg-gray-100 rounded-full p-1">
            <button
              type="button"
              onClick={() => setAnnual(false)}
              aria-pressed={!annual}
              className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${
                !annual ? "bg-white text-gray-900 shadow" : "text-gray-700"
              }`}
            >
              {t("landing.pricing.monthly")}
            </button>
            <button
              type="button"
              onClick={() => setAnnual(true)}
              aria-pressed={annual}
              className={`px-4 py-1.5 text-sm font-semibold rounded-full transition-colors ${
                annual ? "bg-white text-gray-900 shadow" : "text-gray-700"
              }`}
            >
              {t("landing.pricing.annual")} <span className="text-[#6A0DAD] ml-1">{t("landing.pricing.annualSave")}</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 flex flex-col">
            <div className="mb-6">
              <h4 className="text-xl font-bold text-gray-900">{t("landing.pricing.basic")}</h4>
              <p className="text-sm text-gray-500 mt-1">{t("landing.pricing.free")}</p>
            </div>
            <div className="mb-6">
              <span className="text-4xl font-extrabold text-gray-900">0 EUR</span>
              <span className="text-sm text-gray-500 ml-2">{t("landing.pricing.freeSuffix")}</span>
            </div>
            <ul className="space-y-3 text-sm text-gray-700 mb-8 flex-1">
              {features.map((f, i) => {
                const label = f.freeLabel || f.label;
                return (
                  <li key={i} className="flex items-start gap-2">
                    {f.free ? (
                      <Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <X size={16} className="text-gray-300 mt-0.5 flex-shrink-0" />
                    )}
                    <span className={f.free ? "" : "text-gray-600 line-through"}>{label}</span>
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              onClick={() => goToAuth("signup")}
              className="w-full bg-gray-100 text-gray-900 px-6 py-3 rounded-full font-semibold hover:bg-gray-200 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
            >
              {t("landing.pricing.startFree")}
            </button>
          </div>

          <div className="relative rounded-2xl border-2 border-[#9B30FF] bg-gradient-to-br from-purple-50 via-white to-white p-8 flex flex-col shadow-lg">
            <span className="absolute -top-3 right-6 bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full inline-flex items-center gap-1">
              <Crown size={12} /> {t("landing.pricing.premium")}
            </span>
            <div className="mb-6">
              <h4 className="text-xl font-bold text-gray-900">{t("landing.pricing.premium")}</h4>
            </div>
            <div className="mb-6">
              <span className="text-4xl font-extrabold text-gray-900">
                {annual ? `${annualMonthly} EUR` : `${monthly} EUR`}
              </span>
              <span className="text-sm text-gray-500 ml-2">
                {t("landing.pricing.perMonth")}{annual ? ` ${t("landing.pricing.perYearNote")}` : ""}
              </span>
            </div>
            <ul className="space-y-3 text-sm text-gray-700 mb-8 flex-1">
              {features.map((f, i) => {
                const label = f.premiumLabel || f.label;
                return (
                  <li key={i} className="flex items-start gap-2">
                    {f.premium ? (
                      <Check size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <X size={16} className="text-gray-300 mt-0.5 flex-shrink-0" />
                    )}
                    <span className={f.premium ? "" : "text-gray-500 line-through"}>{label}</span>
                  </li>
                );
              })}
            </ul>
            <button
              type="button"
              data-testid="pricing-premium-cta"
              onClick={() => onSelectPremium(annual ? "annual" : "monthly")}
              className="w-full bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
            >
              {t("landing.pricing.getPremium")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -----------------------------------------------------------
 * CTA + Footer
 * --------------------------------------------------------- */

export const CTASection = ({ onGetStarted, onLiveDemo }) => {
  const { t } = useLanguage();
  return (
  <section id="d07-cta" className="relative py-20 md:py-24 overflow-hidden">
    <div className="absolute inset-0 reflecta-grid-bg opacity-40" aria-hidden="true" />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] opacity-5 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <ReflectaLogo className="h-14 md:h-16 w-auto mx-auto mb-6 md:mb-8" />
      <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 tracking-tight">
        {t("landing.cta.headline")}
      </h2>
      <p className="text-lg md:text-xl text-gray-600 mb-8 md:mb-10 max-w-2xl mx-auto">
        {t("landing.cta.description")}
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          type="button"
          onClick={onGetStarted}
          className="bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-purple-500/40 transition-all transform hover:-translate-y-1 flex items-center justify-center group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
        >
          {t("landing.cta.button")}
          <ChevronRight className="ml-2 group-hover:translate-x-1 transition-transform" />
        </button>
        <button
          type="button"
          onClick={onLiveDemo}
          data-testid="d07-try-demo"
          className="bg-white text-gray-900 border border-gray-200 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
        >
          <Sparkles size={18} className="text-[#9B30FF]" />
          {t("landing.pricing.startFree")}
        </button>
      </div>
      <p className="text-sm text-gray-600 mt-6">{t("landing.hero.disclaimer")}</p>
    </div>
  </section>
  );
};

export const Footer = () => {
  const { t } = useLanguage();
  return (
  <footer className="bg-white border-t border-gray-100 py-10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
      <div className="flex items-center gap-2">
        <ReflectaLogo className="h-6 w-auto grayscale opacity-50" />
        <span className="text-gray-600 text-sm font-medium">{t("landing.footer.copyright")}</span>
      </div>
      <nav aria-label="Legal" className="flex flex-wrap justify-center items-center gap-x-6 gap-y-3 text-sm text-gray-600 font-medium">
        <a href="/datenschutz" className="hover:text-[#6A0DAD] inline-block py-2 px-1">{t("landing.footer.datenschutz")}</a>
        <a href="/agb" className="hover:text-[#6A0DAD] inline-block py-2 px-1">{t("landing.footer.agb")}</a>
        <a href="/impressum" className="hover:text-[#6A0DAD] inline-block py-2 px-1">{t("landing.footer.impressum")}</a>
        <button type="button" onClick={scrollToTop} className="hover:text-[#6A0DAD] inline-block py-2 px-1">
          {t("landing.footer.backTop")}
        </button>
        <button type="button" onClick={reopenCookieSettings} className="hover:text-[#6A0DAD] inline-block py-2 px-1">
          {t("landing.footer.cookieSettings")}
        </button>
        <LanguageToggle solid={true} />
      </nav>
    </div>
  </footer>
  );
};


/* -----------------------------------------------------------
 * Mercury-style email capture
 * --------------------------------------------------------- */

export const EmailCapture = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [optOut, setOptOut] = useState(false);
  const [shown, setShown] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!shown && email.length >= 3 && email.includes("@")) {
      setShown(true);
    }
  }, [email, shown]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await captureLeadEmail({ email, marketing_consent: !optOut });
    } catch {
      // Silently ignore — still redirect even if capture fails
    }
    const params = new URLSearchParams({ mode: "signup", email });
    window.location.href = `/auth?${params.toString()}`;
  };

  return (
    <form onSubmit={onSubmit} className="w-full" data-testid="hero-email-capture">
      {/* Desktop: side-by-side pill. Mobile: stacked. */}
      <div className="flex flex-col md:flex-row gap-3 md:gap-0 md:bg-white md:rounded-full md:shadow-lg md:border md:border-gray-200 md:p-1.5">
        <div className="flex-1 md:min-w-0">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("landing.hero.emailPlaceholder")}
            aria-label={t("auth.email")}
            className="w-full min-h-[48px] md:min-h-[52px] bg-white rounded-full md:rounded-full px-5 text-base text-gray-900 placeholder:text-gray-400 border border-gray-200 md:border-0 focus:outline-none focus:ring-2 focus:ring-[#9B30FF] focus:ring-offset-0 md:focus:ring-0 shadow-sm md:shadow-none"
            style={{ boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)" }}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full md:w-auto min-h-[48px] md:min-h-[52px] bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] text-white px-7 rounded-full font-bold text-base hover:shadow-2xl hover:shadow-purple-500/40 transition-all transform md:hover:-translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2 inline-flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-70"
        >
          {loading ? "..." : t("landing.hero.cta")}
          {!loading && <ChevronRight size={18} />}
        </button>
      </div>

      {/* Slide-in opt-out card. */}
      <div
        className="overflow-hidden transition-all duration-300 ease-out"
        style={{
          maxHeight: shown ? "200px" : "0px",
          opacity: shown ? 1 : 0,
          marginTop: shown ? "12px" : "0px",
        }}
        aria-hidden={!shown}
        data-testid="hero-optout-card"
      >
        <div className="bg-purple-50/70 border border-purple-100 text-gray-700 rounded-2xl px-4 py-3 text-sm shadow-sm backdrop-blur-sm">
          <p className="leading-snug mb-2">
            {t("landing.email.optOutBody")}
          </p>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={optOut}
              onChange={(e) => setOptOut(e.target.checked)}
              className="w-4 h-4 accent-[#9B30FF] rounded"
              aria-label={t("landing.email.optOutLabel")}
            />
            <span className="text-gray-900 font-medium">{t("landing.email.optOutLabel")}</span>
          </label>
        </div>
      </div>
    </form>
  );
};

/* -----------------------------------------------------------
 * Top-level page
 * --------------------------------------------------------- */

function setMetaTag(name, content) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/* -----------------------------------------------------------
 * Production landing page - Shopify-style full-bleed video hero
 * with kinetic phrase rotation, two-state sticky nav, and a
 * rounded-top transition into the rest of the marketing page.
 * --------------------------------------------------------- */

const HERO_KINETIC_TRANSITION_MS = 620;
const HERO_KINETIC_HOLD_MS = 4500;

const ReflectaLogoColor = ({ className = "h-10 w-auto" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 200 60"
    className={className}
    aria-label="Reflecta"
    role="img"
  >
    <defs>
      <linearGradient id="reflectaHeroGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#9B30FF" />
        <stop offset="100%" stopColor="#6A0DAD" />
      </linearGradient>
    </defs>
    <rect x="10" y="10" width="40" height="40" rx="10" ry="10" fill="url(#reflectaHeroGrad)" />
    <line x1="30" y1="10" x2="30" y2="50" stroke="#FFFFFF" strokeWidth="1.5" />
    <path d="M20 25 Q 25 18, 30 25 Q 35 32, 40 25" stroke="#FFFFFF" strokeWidth="2" fill="none" />
    <path
      d="M20 35 Q 25 42, 30 35 Q 35 28, 40 35"
      stroke="#FFFFFF"
      strokeWidth="2"
      fill="none"
      opacity="0.7"
    />
    <text
      x="60"
      y="38"
      fontFamily="'Plus Jakarta Sans', system-ui, sans-serif"
      fontWeight="600"
      fontSize="24"
      fill="#171717"
    >
      Reflecta
    </text>
  </svg>
);

const ReflectaLogoWhite = ({ className = "h-10 w-auto" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 200 60"
    className={className}
    aria-label="Reflecta"
    role="img"
  >
    <rect
      x="10"
      y="10"
      width="40"
      height="40"
      rx="10"
      ry="10"
      fill="rgba(255,255,255,0.18)"
      stroke="rgba(255,255,255,0.85)"
      strokeWidth="1"
    />
    <line x1="30" y1="10" x2="30" y2="50" stroke="#FFFFFF" strokeWidth="1.5" />
    <path
      d="M20 25 Q 25 18, 30 25 Q 35 32, 40 25"
      stroke="#FFFFFF"
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M20 35 Q 25 42, 30 35 Q 35 28, 40 35"
      stroke="#FFFFFF"
      strokeWidth="2"
      fill="none"
      opacity="0.7"
    />
    <text
      x="60"
      y="38"
      fontFamily="'Plus Jakarta Sans', system-ui, sans-serif"
      fontWeight="600"
      fontSize="24"
      fill="#FFFFFF"
    >
      Reflecta
    </text>
  </svg>
);

const KineticPhraseWhite = ({ holdMs }) => {
  const reduce = useMemo(() => prefersReducedMotion(), []);
  const { t } = useLanguage();
  const phrases = useMemo(
    () => [t("landing.hero.phrase1"), t("landing.hero.phrase2"), t("landing.hero.phrase3")],
    [t]
  );
  const [current, setCurrent] = useState(0);
  const [previous, setPrevious] = useState(null);

  useEffect(() => {
    if (reduce) return undefined;
    const hold = setTimeout(() => {
      const nextIdx = (current + 1) % phrases.length;
      setPrevious(current);
      setCurrent(nextIdx);
      const clear = setTimeout(() => setPrevious(null), HERO_KINETIC_TRANSITION_MS);
      return () => clearTimeout(clear);
    }, holdMs);
    return () => clearTimeout(hold);
  }, [current, reduce, holdMs, phrases.length]);

  return (
    <span
      className="relative block w-full overflow-hidden h-[3em] lg:h-[1.4em] pb-[0.1em]"
      aria-live="polite"
      style={{ lineHeight: 1.1 }}
    >
      <span className="sr-only">{phrases[current]}</span>
      {previous !== null && (
        <span
          aria-hidden="true"
          className="absolute inset-0 flex items-start text-white reflecta-kinetic-out"
          style={{ lineHeight: 1.1 }}
        >
          <span className="block w-full">{phrases[previous]}</span>
        </span>
      )}
      <span
        aria-hidden="true"
        className={`absolute inset-0 flex items-start text-white ${
          previous !== null ? "reflecta-kinetic-in" : ""
        }`}
        style={{ lineHeight: 1.1 }}
      >
        <span className="block w-full">{phrases[current]}</span>
      </span>
    </span>
  );
};

const useScrolledPast = (px = 80) => {
  const [past, setPast] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.scrollY > px;
  });
  useEffect(() => {
    const handler = () => setPast(window.scrollY > px);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, [px]);
  return past;
};

const LanguageToggle = ({ solid }) => {
  const { language, setLanguage } = useLanguage();
  const btnBase =
    "text-xs font-semibold px-2 py-1 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";
  const activeCls = solid
    ? "text-[#6A0DAD]"
    : "text-white";
  const inactiveCls = solid
    ? "text-gray-400 hover:text-gray-600"
    : "text-white/60 hover:text-white/90";
  const ringCls = solid ? "focus-visible:ring-[#9B30FF]" : "focus-visible:ring-white";
  return (
    <div className="flex items-center gap-1" aria-label="Language switcher">
      <button
        type="button"
        onClick={() => setLanguage("en")}
        aria-pressed={language === "en"}
        className={`${btnBase} ${ringCls} ${language === "en" ? activeCls : inactiveCls}`}
      >
        EN
      </button>
      <span className={solid ? "text-gray-300" : "text-white/40"} aria-hidden="true">/</span>
      <button
        type="button"
        onClick={() => setLanguage("de")}
        aria-pressed={language === "de"}
        className={`${btnBase} ${ringCls} ${language === "de" ? activeCls : inactiveCls}`}
      >
        DE
      </button>
    </div>
  );
};

const HeroNavbar = () => {
  const solid = useScrolledPast(80);
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (!isOpen) {
      document.body.classList.remove("reflecta-no-scroll");
      return undefined;
    }
    document.body.classList.add("reflecta-no-scroll");
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.classList.remove("reflecta-no-scroll");
    };
  }, [isOpen]);

  const linkBase =
    "font-medium transition-colors text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded px-1";

  return (
    <nav
      aria-label="Primary"
      className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${
        solid
          ? "bg-white/90 backdrop-blur-md border-b border-gray-100"
          : "bg-transparent border-b border-transparent"
      }`}
      style={{
        boxShadow: solid ? "0 1px 3px rgba(17, 17, 17, 0.04)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className={`flex-shrink-0 flex items-center gap-3 cursor-pointer focus:outline-none focus-visible:ring-2 rounded ${
              solid ? "focus-visible:ring-[#9B30FF]" : "focus-visible:ring-white"
            }`}
            aria-label="Reflecta - back to top"
          >
            {solid ? (
              <ReflectaLogoColor className="h-9 md:h-10 w-auto" />
            ) : (
              <ReflectaLogoWhite className="h-9 md:h-10 w-auto" />
            )}
            <span
              className={`hidden sm:inline-block text-xs font-semibold tracking-wide uppercase border-l pl-3 leading-tight ${
                solid
                  ? "text-[#6A0DAD] border-purple-200"
                  : "text-white/90 border-white/30"
              }`}
            >
              {t("landing.hero.claim")}
            </span>
          </button>

          <div className="hidden md:flex items-center space-x-8">
            <button
              type="button"
              onClick={() => scrollToId("features")}
              className={`${linkBase} ${
                solid
                  ? "text-gray-600 hover:text-[#6A0DAD] focus-visible:ring-[#9B30FF]"
                  : "text-white/90 hover:text-white focus-visible:ring-white"
              }`}
            >
              {t("landing.nav.features")}
            </button>
            <button
              type="button"
              onClick={() => scrollToId("pricing")}
              className={`${linkBase} ${
                solid
                  ? "text-gray-600 hover:text-[#6A0DAD] focus-visible:ring-[#9B30FF]"
                  : "text-white/90 hover:text-white focus-visible:ring-white"
              }`}
            >
              {t("landing.nav.pricing")}
            </button>
            <div className="flex items-center space-x-4 ml-4">
              <LanguageToggle solid={solid} />
              <button
                type="button"
                onClick={() => goToAuth("login")}
                className={`font-medium transition-colors text-sm focus:outline-none focus-visible:ring-2 rounded px-1 ${
                  solid
                    ? "text-gray-900 hover:text-[#6A0DAD] focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
                    : "text-white hover:text-white/80 focus-visible:ring-white"
                }`}
              >
                {t("landing.nav.login")}
              </button>
              <button
                type="button"
                onClick={() => goToAuth("signup")}
                className={`px-5 py-2.5 rounded-full font-semibold transition-all text-sm focus:outline-none focus-visible:ring-2 ${
                  solid
                    ? "bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] text-white hover:shadow-lg hover:shadow-purple-500/30 transform hover:-translate-y-0.5 focus-visible:ring-[#9B30FF] focus-visible:ring-offset-2"
                    : "bg-white text-gray-900 hover:bg-white/90 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                }`}
              >
                {t("landing.nav.signup")}
              </button>
            </div>
          </div>

          <div className="md:hidden flex items-center">
            <button
              type="button"
              onClick={() => setIsOpen((v) => !v)}
              className={`p-2 -mr-2 focus:outline-none focus-visible:ring-2 rounded ${
                solid
                  ? "text-gray-700 focus-visible:ring-[#9B30FF]"
                  : "text-white focus-visible:ring-white"
              }`}
              aria-expanded={isOpen}
              aria-controls="hero-mobile-drawer"
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden fixed inset-x-0 top-16 bottom-0 z-40">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setIsOpen(false)}
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            tabIndex={-1}
          />
          <div
            id="hero-mobile-drawer"
            role="dialog"
            aria-modal="true"
            className="reflecta-drawer-panel relative bg-white border-b border-gray-100 px-4 pt-3 pb-6 space-y-1 shadow-xl"
          >
            {[
              ["features", t("landing.nav.features")],
              ["pricing", t("landing.nav.pricing")],
            ].map(([id, label]) => (
              <button
                key={id}
                onClick={() => {
                  setIsOpen(false);
                  scrollToId(id);
                }}
                className="block w-full text-left px-3 py-3 text-base font-medium text-gray-700 hover:bg-purple-50 rounded-md min-h-[44px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9B30FF]"
              >
                {label}
              </button>
            ))}
            <div className="px-3 py-2">
              <LanguageToggle solid={true} />
            </div>
            <button
              onClick={() => {
                setIsOpen(false);
                goToAuth("login");
              }}
              className="block w-full text-left px-3 py-3 text-base font-medium text-gray-700 hover:bg-purple-50 rounded-md min-h-[44px]"
            >
              {t("landing.nav.login")}
            </button>
            <button
              onClick={() => {
                setIsOpen(false);
                goToAuth("signup");
              }}
              className="block w-full text-center mt-3 bg-gradient-to-r from-[#9B30FF] to-[#6A0DAD] text-white px-6 py-3 rounded-xl font-medium min-h-[44px]"
            >
              {t("landing.nav.signup")}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

/* -----------------------------------------------------------
 * Video star mask
 *
 * The hero video has a small generative-AI sparkle baked into its
 * bottom-right corner at roughly (1160, 600) in the 1280x720 source.
 * This component mirrors object-cover scaling and pins a small Reflecta
 * brand mark exactly OVER the sparkle so it disappears at all viewports.
 * --------------------------------------------------------- */

const VIDEO_W = 1280;
const VIDEO_H = 720;
const STAR_SRC_CX = 1160;
const STAR_SRC_CY = 600;
const STAR_SRC_SIZE = 60;

const VideoStarMask = ({ heroRef, videoRef }) => {
  const [pos, setPos] = useState(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || typeof window === "undefined") return undefined;

    const compute = () => {
      const rect = hero.getBoundingClientRect();
      const W = rect.width;
      const H = rect.height;
      if (!W || !H) return;

      const scale = Math.max(W / VIDEO_W, H / VIDEO_H);
      const renderedW = VIDEO_W * scale;
      const renderedH = VIDEO_H * scale;
      const offsetX = (W - renderedW) / 2;
      const offsetY = (H - renderedH) / 2;

      const cxPx = offsetX + STAR_SRC_CX * scale;
      const cyPx = offsetY + STAR_SRC_CY * scale;
      const sizePx = Math.max(40, Math.min(72, STAR_SRC_SIZE * scale));

      setPos({ cxPx, cyPx, sizePx });
    };

    compute();
    const ro = new ResizeObserver(() => compute());
    ro.observe(hero);
    window.addEventListener("resize", compute);
    const v = videoRef.current;
    const onMeta = () => compute();
    if (v) v.addEventListener("loadedmetadata", onMeta);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
      if (v) v.removeEventListener("loadedmetadata", onMeta);
    };
  }, [heroRef, videoRef]);

  if (!pos) return null;

  return (
    <div
      aria-hidden="true"
      className="absolute z-10 pointer-events-none"
      style={{
        left: `${pos.cxPx}px`,
        top: `${pos.cyPx}px`,
        width: `${pos.sizePx}px`,
        height: `${pos.sizePx}px`,
        transform: "translate(-50%, -50%)",
        opacity: 1,
        filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.35))",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 40 40"
        width="100%"
        height="100%"
        aria-hidden="true"
      >
        <defs>
          <linearGradient
            id="reflectaWmGrad"
            x1="0"
            y1="0"
            x2="40"
            y2="40"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor="#9B30FF" />
            <stop offset="100%" stopColor="#6A0DAD" />
          </linearGradient>
        </defs>
        <rect
          x="2"
          y="2"
          width="36"
          height="36"
          rx="9"
          ry="9"
          fill="url(#reflectaWmGrad)"
        />
        <line
          x1="20"
          y1="4"
          x2="20"
          y2="36"
          stroke="#FFFFFF"
          strokeOpacity="0.55"
          strokeWidth="1"
        />
        <path
          d="M9 17 Q 14 11, 20 17 Q 26 23, 31 17"
          stroke="#FFFFFF"
          strokeWidth="1.9"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M9 25 Q 14 31, 20 25 Q 26 19, 31 25"
          stroke="#FFFFFF"
          strokeOpacity="0.7"
          strokeWidth="1.9"
          fill="none"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
};

const VideoHero = () => {
  const reduce = useMemo(() => prefersReducedMotion(), []);
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const heroRef = useRef(null);
  // Mount the <video> element only after first paint. Keeps the 2MB mp4 off
  // the critical render path so Lighthouse Performance does not hold the
  // entire landing page hostage to video decode + network. The static
  // gradient stays as the immediate hero background.
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const idle =
      typeof window !== "undefined" && window.requestIdleCallback
        ? window.requestIdleCallback
        : (cb) => setTimeout(cb, 200);
    const cancel =
      typeof window !== "undefined" && window.cancelIdleCallback
        ? window.cancelIdleCallback
        : clearTimeout;
    const handle = idle(() => {
      if (!cancelled) setShowVideo(true);
    });
    return () => {
      cancelled = true;
      try {
        cancel(handle);
      } catch (e) {
        // ignore
      }
    };
  }, [reduce]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    if (reduce) {
      try {
        video.pause();
      } catch (e) {
        // ignore
      }
    }
    return undefined;
  }, [reduce, showVideo]);

  return (
    <section
      ref={heroRef}
      className="relative w-full overflow-hidden bg-black"
      style={{
        height: "min(92vh, 940px)",
        minHeight: "640px",
        // Static fallback background so the hero has weight on first paint
        // before the video element mounts.
        backgroundImage:
          "linear-gradient(180deg, #1a0f2b 0%, #0d0617 60%, #050308 100%)",
      }}
      aria-label="Hero"
    >
      {showVideo && (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay={!reduce}
          muted
          loop
          playsInline
          preload={reduce ? "auto" : "none"}
          aria-hidden="true"
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
      )}

      <div
        className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/70"
        aria-hidden="true"
      />

      <VideoStarMask heroRef={heroRef} videoRef={videoRef} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center h-full pt-20 md:pt-24 pb-16 md:pb-24">
        <div className="max-w-3xl lg:max-w-4xl">
          <h1 className="sr-only">{t("landing.hero.srHeading")}</h1>
          <div
            aria-hidden="true"
            className="font-extrabold tracking-tight text-white"
            style={{ letterSpacing: "-0.02em" }}
          >
            <span
              className="block"
              style={{
                fontSize: "clamp(1.75rem, 5.8vw, 4.5rem)",
                lineHeight: 1.05,
                textWrap: "balance",
              }}
            >
              {t("landing.hero.headline")}
            </span>
            <span
              className="block mt-2 sm:mt-3"
              style={{
                fontSize: "clamp(1.375rem, 2.9vw, 2.375rem)",
                lineHeight: 1.15,
              }}
            >
              <KineticPhraseWhite holdMs={HERO_KINETIC_HOLD_MS} />
            </span>
          </div>
        </div>

        <div className="max-w-xl mt-6 sm:mt-8">
          <p className="text-base sm:text-lg text-white/85 leading-relaxed">
            {t("landing.hero.description")}
          </p>

          <div className="mt-7 sm:mt-9 reflecta-hero-email-light">
            <EmailCapture />
          </div>
          <p className="mt-3 text-xs text-white/70">
            {t("landing.hero.disclaimer")}
          </p>
        </div>
      </div>
    </section>
  );
};

export default function LandingPage() {
  const { t } = useLanguage();
  const handleLiveDemo = useCallback(() => enterDemo(), []);
  const handleGetStarted = useCallback(() => goToAuth("signup"), []);
  const handleSelectPremium = useCallback((plan) => {
    setPremiumIntent(plan);
    goToAuth("signup");
  }, []);

  useEffect(() => {
    document.title = t("landing.seo.title");
    setMetaTag("description", t("landing.seo.description"));
    setMetaTag("robots", "index,follow");
  }, [t]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const section =
      params.get("section") || window.location.hash.replace(/^#/, "");
    if (!section) return;
    const doScroll = () => {
      const el = document.getElementById(section);
      if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
    };
    const t1 = setTimeout(doScroll, 50);
    const t2 = setTimeout(doScroll, 1500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans text-[#171717] selection:bg-purple-200 selection:text-purple-900 antialiased">
      <HeroNavbar />
      <main>
        <VideoHero />
        {/* Rounded-top wrapper that overlaps the bottom of the dark hero,
            mirroring Shopify's "the next section pokes up with rounded
            shoulders" transition. The wrapper is white with the same
            rounded radius as the device frame above. -mt-* pulls it up
            into the hero so the rounded corners appear over the dark
            video. */}
        <div className="relative z-20 bg-white rounded-t-[28px] sm:rounded-t-[36px] lg:rounded-t-[44px] overflow-hidden -mt-6 sm:-mt-8 lg:-mt-12 shadow-[0_-12px_40px_-20px_rgba(17,17,17,0.25)]">
          <MarqueeSection />
          <FeatureSection onExploreAnalytics={handleLiveDemo} />
          <PricingSection onSelectPremium={handleSelectPremium} />
          <CTASection onGetStarted={handleGetStarted} onLiveDemo={handleLiveDemo} />
        </div>
      </main>
      <Footer />
    </div>
  );
}
